import { useNavigate, useOutletContext } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { appendPracticeToOrganization } from 'api/Practice';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import LocationSelect from './components/LocationSelect';
import OrganizationCard from './components/OrganizationCard';
import SpecificDataTabs from './components/SpecificDataTabs';
import ImportTypeSelector from './components/ImportTypeSelector';
import ImportConfirmationModal from './components/ImportConfirmationModal';

const IMPORT_TYPES = {
  ALL: 'all',
  NONE: 'none',
  SPECIFIC: 'specific'
};

const ImportFromOrganization = ({
  addFooterActions,
  setHeaderButtonProps,
  mutateOrchestratorData,
  orchestratorLoading,
  navigateToSubstep,
  onNext,
  onPrev
}) => {
  const { practice } = useOutletContext();
  const navigate = useNavigate();

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [importType, setImportType] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [importData, setImportData] = useState({
    patients: [],
    services: [],
    macros: [],
    packages: [],
    products: []
  });

  const organization = practice?.organization;

  const { mutate: importDataFromOrganization, isPending } = useCustomMutation({
    mutationFn: (data) => appendPracticeToOrganization(navigate, data),
    onSuccess: () => {
      mutateOrchestratorData({ importChoice: importType });
    },
    invalidateQueryKey: ['practice'],
    successMessage: 'Data imported',
    errorMessage: "Couldn't import data"
  });

  useEffect(() => {
    if (!organization?.id) {
      navigateToSubstep('onboarding__basic_setup_confirmation__organization');
    }
  }, [organization?.id, navigateToSubstep]);

  const canContinue = useMemo(() => {
    if (!selectedLocation) return false;
    if (importType === IMPORT_TYPES.NONE) return true;
    if (importType === IMPORT_TYPES.ALL) return true;
    if (importType === IMPORT_TYPES.SPECIFIC) {
      const hasData = Object.values(importData).some((arr) => arr.length > 0);
      return hasData;
    }
    return false;
  }, [selectedLocation, importType, importData]);

  const handleContinue = useCallback(() => {
    if (importType === IMPORT_TYPES.NONE) {
      mutateOrchestratorData({ importChoice: 'none' });
      return;
    }

    importDataFromOrganization({
      importAll: importType === IMPORT_TYPES.ALL,
      ownerPracticeId: organization?.id,
      practiceId: practice.id,
      selectedLocation: selectedLocation?.value,
      userId: organization?.user_id,
      ...(importType === IMPORT_TYPES.SPECIFIC ? { data: importData } : {})
    });
  }, [
    importType,
    mutateOrchestratorData,
    importDataFromOrganization,
    organization,
    practice,
    selectedLocation,
    importData
  ]);

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick: canContinue ? handleContinue : () => setShowConfirmation(true),
        isDisabled: orchestratorLoading || isPending
      }
    ]);
  }, [
    selectedLocation,
    importType,
    importData,
    onPrev,
    orchestratorLoading,
    isPending,
    canContinue,
    handleContinue,
    setHeaderButtonProps,
    addFooterActions
  ]);

  return (
    <div className="flex h-full flex-col gap-6 pb-5">
      <ImportConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onMarkComplete={() => {
          setShowConfirmation(false);
          mutateOrchestratorData({ importChoice: 'none' });
        }}
        onSkip={() => {
          setShowConfirmation(false);
          onNext();
        }}
        isLoading={orchestratorLoading}
      />

      <OrganizationCard organization={organization} />

      <LocationSelect
        organization={organization}
        practiceId={practice?.id}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
      />

      {selectedLocation && (
        <ImportTypeSelector
          importType={importType}
          setImportType={setImportType}
          importTypes={IMPORT_TYPES}
        />
      )}

      {selectedLocation && importType === IMPORT_TYPES.SPECIFIC && (
        <SpecificDataTabs
          practiceId={practice?.id}
          selectedLocation={selectedLocation}
          importData={importData}
          setImportData={setImportData}
        />
      )}
    </div>
  );
};

export default ImportFromOrganization;
